import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { Text } from '@/components/ui/catalyst/text';

type ChannelType = 'fax' | 'email';

interface FollowUpStrategySectionProps {
  enabled: boolean;
  channel: ChannelType;
  intervalDays: number;
  maxAttempts: number;
  disabled?: boolean;
  disabledMessage?: string;
  onToggle: (enabled: boolean) => void;
  onChannelChange: (channel: ChannelType) => void;
  onIntervalChange: (days: number) => void;
  onMaxAttemptsChange: (attempts: number) => void;
}

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 5, label: '5 days' },
  { value: 7, label: '7 days' },
  { value: 10, label: '10 days' },
  { value: 14, label: '14 days' },
];

const MAX_ATTEMPTS_OPTIONS = [
  { value: 2, label: '2 attempts' },
  { value: 3, label: '3 attempts' },
  { value: 4, label: '4 attempts' },
  { value: 5, label: '5 attempts' },
];

export const toFollowUpChannel = (
  channel: string | null | undefined,
): ChannelType => {
  if (channel === 'email' || channel === 'fax') return channel;
  return 'fax';
};

export const FollowUpStrategySection = ({
  enabled,
  channel,
  intervalDays,
  maxAttempts,
  disabled = false,
  disabledMessage,
  onToggle,
  onChannelChange,
  onIntervalChange,
  onMaxAttemptsChange,
}: FollowUpStrategySectionProps) => {
  let statusLabel = 'Disabled';
  if (disabled) {
    statusLabel = 'Ineligible';
  } else if (enabled) {
    statusLabel = 'Enabled';
  }

  return (
    <div className="space-y-2.5 font-sans">
    <div className="flex items-center justify-between gap-4">
      <Text className="font-sans text-[14px] font-semibold leading-[18px] text-zinc-900">
        Automatic Follow-ups
      </Text>
      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle automatic follow-ups"
          onClick={() => {
            if (!disabled) {
              onToggle(!enabled);
            }
          }}
          className={`relative inline-flex h-[18px] w-8 items-center rounded-full border border-transparent p-[2px] transition-colors ${
            enabled ? 'bg-violet-600' : 'bg-zinc-200'
          }`}
          disabled={disabled}
        >
          <span
            className={`block h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform ${
              enabled ? 'translate-x-[14px]' : 'translate-x-0'
            }`}
          />
        </button>
        <Text className="font-sans text-[12px] font-medium leading-4 text-zinc-500">
          {statusLabel}
        </Text>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <label className="block" htmlFor="follow-up-interval">
        <span className="mb-1 block font-sans text-[12px] font-medium leading-4 text-zinc-500">
          Send every
        </span>
        <select
          id="follow-up-interval"
          value={intervalDays}
          onChange={(event) => onIntervalChange(Number(event.target.value))}
          className="h-9 w-full rounded-[10px] border border-zinc-200 bg-white px-3 font-sans text-[14px] leading-[18px] text-zinc-900 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          disabled={!enabled || disabled}
        >
          {INTERVAL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block" htmlFor="follow-up-channel">
        <span className="mb-1 block font-sans text-[12px] font-medium leading-4 text-zinc-500">
          Via channel
        </span>
        <select
          id="follow-up-channel"
          value={channel}
          onChange={(event) =>
            onChannelChange(event.target.value as ChannelType)
          }
          className="h-9 w-full rounded-[10px] border border-zinc-200 bg-white px-3 font-sans text-[14px] leading-[18px] text-zinc-900 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          disabled={!enabled || disabled}
        >
          <option value="fax">Fax</option>
          <option value="email">Email</option>
        </select>
      </label>

      <label className="block" htmlFor="follow-up-attempts">
        <span className="mb-1 block font-sans text-[12px] font-medium leading-4 text-zinc-500">
          Max attempts
        </span>
        <select
          id="follow-up-attempts"
          value={maxAttempts}
          onChange={(event) => onMaxAttemptsChange(Number(event.target.value))}
          className="h-9 w-full rounded-[10px] border border-zinc-200 bg-white px-3 font-sans text-[14px] leading-[18px] text-zinc-900 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          disabled={!enabled || disabled}
        >
          {MAX_ATTEMPTS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>

    <div className="flex items-start gap-2 rounded-[10px] border border-zinc-100 bg-zinc-50 px-3.5 py-2">
      <InformationCircleIcon className="mt-px h-[14px] w-[14px] flex-shrink-0 text-zinc-400" />
      <Text className="font-sans text-[12px] leading-4 text-zinc-500">
        {disabledMessage ||
          'Follow-ups stop when Voice AI confirms acknowledgment, request status advances, or max attempts reached.'}
      </Text>
    </div>
    </div>
  );
};
